<!--
Vue Wishlist Component (Wishlist 2.0)

Usage:
1. Add class "vue-wishlist" and data attributes to any element:
   <button class="vue-wishlist" data-product-id="123" data-in-wishlist="1">
       <i class="ico ico-heart"></i>
   </button>
2. The component will mount on click, replacing the element.
3. Required: data-product-id
4. Optional: data-in-wishlist, data-text, data-mobile-text, data-orientation
-->
<template>
    <div v-if="isLogged && productId" class="c-wishlist-popup">
        <button
            ref="trigger"
            type="button"
            class="c-wishlist-popup__trigger"
            :class="{ 'is-wishlisted': isInAnyWishlist, 'is-active': isPopupVisible }"
            @click.stop.prevent="toggleWishlist"
        >
            <i :class="['ico', isInAnyWishlist ? 'ico-heart-full' : 'ico-heart']"></i>

            <template v-if="text">
                <span class="sc-text">{{ text }}</span>
                <span v-if="mobileText" class="sc-text-mobile">{{ mobileText }}</span>
            </template>
        </button>

        <div
            ref="popupContent"
            class="c-wishlist-popup__content js-wishlist-popup-content"
            :class="[
                'is-' + orientation,
                { 'is-active': isPopupVisible, 'is-align-right': alignRight },
            ]"
        >
            <div
                class="wishlist-popup js-wishlist-popup"
                :class="{ 'is-empty': !wishlists || wishlists.length === 0 }"
            >
                <button
                    type="button"
                    class="c-wishlist-popup__close js-wishlist-close"
                    @click="closePopup"
                >
                    <i class="ico ico-close"></i>
                </button>

                <div
                    class="wishlist-popup__list js-wishlist-popup-list"
                    :data-product-id="productId"
                >
                    <h3 class="wishlist-popup__list__title">{{ $t("Wishlists.selectList") }}</h3>

                    <form class="c-form wishlist-popup__content">
                        <template v-if="wishlists && wishlists.length > 0">
                            <div v-for="wish in wishlists" :key="wish.id">
                                <div
                                    class="c-form__item c-form__item--checkbox js-wishlist-popup-item"
                                >
                                    <input
                                        :id="`wish_${wish.id}_${productId}_${uid}`"
                                        type="checkbox"
                                        name="customer_wishlist_id"
                                        :checked="wish.is_present === '1'"
                                        :value="wish.id"
                                        class="c-form__input js-wishlist-popup-checkbox"
                                        data-custom="custom"
                                        :data-product-id="productId"
                                        @change="(event) => toggleWishlistItem(event, wish)"
                                    />
                                    <label
                                        class="c-form__label"
                                        :for="`wish_${wish.id}_${productId}_${uid}`"
                                    >
                                        {{ wish.name }}
                                    </label>
                                </div>
                            </div>
                        </template>

                        <div
                            v-if="isLoadingWishlists && !wishlists.length"
                            class="c-wishlist-popup__skeleton"
                        >
                            <div class="sc-item"></div>
                            <div class="sc-item"></div>
                        </div>

                        <p
                            v-if="!isLoadingWishlists && !wishlists.length"
                            class="sc-empty-list-message"
                        >
                            {{ $t("Wishlists.noWishlists") }}
                        </p>
                    </form>
                </div>

                <div class="wishlist-popup__new">
                    <a
                        v-show="!showNewWishlistForm"
                        href="#"
                        class="c-link js-wishlist-new-trigger"
                        @click.prevent="showCreateForm"
                    >
                        <i class="ico ico-plus"></i>
                        <span class="sc-text">{{ $t("Wishlists.createNew") }}</span>
                    </a>

                    <div
                        v-if="showNewWishlistForm"
                        class="wishlist-popup__form js-wishlist-new"
                        style="display: block"
                    >
                        <form
                            class="c-form js-form-wishlist-create"
                            @submit.prevent="createNewWishlist"
                        >
                            <div class="c-form__item">
                                <input
                                    ref="newWishlistInput"
                                    v-model="newWishlistName"
                                    type="text"
                                    name="name"
                                    :placeholder="$t('Wishlists.nameLabel')"
                                    class="c-input js-wishlist-popup-new-input"
                                    required
                                />
                            </div>
                            <button
                                type="submit"
                                class="c-btn c-btn--submit"
                                :disabled="isCreating"
                            >
                                {{ $t("Wishlists.create") }}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div v-else-if="!isLogged" class="c-wishlist-popup">
        <button
            type="button"
            class="c-wishlist-popup__trigger"
            data-toggle="modal"
            data-target="#login-modal"
        >
            <i class="ico ico-heart"></i>
            <template v-if="text">
                <span class="sc-text">{{ text }}</span>
                <span v-if="mobileText" class="sc-text-mobile">{{ mobileText }}</span>
            </template>
        </button>
    </div>
</template>

<script>
import client from "FrontendJsAssets/common/client";

export default {
    name: "VueWishlist",
    props: {
        productId: {
            type: [String, Number],
            default: null,
        },
        inWishlist: {
            type: Boolean,
            default: false,
        },
        text: {
            type: String,
            default: "",
        },
        mobileText: {
            type: String,
            default: "",
        },
        orientation: {
            type: String,
            default: "bottom",
            validator: (value) => ["left", "right", "bottom", "top"].includes(value),
        },
        autoOpen: {
            type: Boolean,
            default: false,
        },
        containerSelector: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            uid: Math.random().toString(36).substring(2, 9),
            isPopupVisible: false,
            newWishlistName: "",
            wishlists: [],
            showNewWishlistForm: false,
            isLoadingWishlists: false,
            isCreating: false,
            alignRight: false,
        };
    },
    computed: {
        isLogged() {
            return window.Eshop?.options?.isLogged === true;
        },
        isInAnyWishlist() {
            if (this.wishlists && this.wishlists.length > 0) {
                return this.wishlists.some((w) => w.is_present === "1");
            }
            return this.inWishlist;
        },
    },
    mounted() {
        document.addEventListener("click", this.handleClickOutside);
        document.addEventListener("wishlist:close-all", this.onCloseAll);
        // Auto-open popup only when mounted via click (wishlist-vue.js)
        if (this.autoOpen && this.isLogged && this.productId) {
            this.openPopup();
        }
    },
    beforeDestroy() {
        document.removeEventListener("click", this.handleClickOutside);
        document.removeEventListener("wishlist:close-all", this.onCloseAll);
    },
    methods: {
        toggleWishlist() {
            if (this.isPopupVisible) {
                this.closePopup();
            } else {
                this.openPopup();
            }
        },
        openPopup() {
            // Close all other wishlist popups first
            document.dispatchEvent(
                new CustomEvent("wishlist:close-all", { detail: { uid: this.uid } })
            );
            this.checkOverflow();
            this.isPopupVisible = true;
            this.fetchWishlists();
        },
        checkOverflow() {
            const trigger = this.$refs.trigger;
            if (!trigger) return;

            if (this.containerSelector) {
                const container = trigger.closest(this.containerSelector);
                if (container) {
                    const triggerRect = trigger.getBoundingClientRect();
                    const containerRect = container.getBoundingClientRect();
                    const triggerX = triggerRect.left - containerRect.left;
                    const containerWidth = containerRect.width;

                    this.alignRight = triggerX + 100 > containerWidth;
                    return;
                }
            }

            const triggerRect = trigger.getBoundingClientRect();
            this.alignRight = triggerRect.left + 100 > window.innerWidth;
        },
        closePopup() {
            this.isPopupVisible = false;
        },
        onCloseAll(event) {
            // Close this popup unless it's the one that triggered the event
            if (event.detail?.uid !== this.uid) {
                this.isPopupVisible = false;
            }
        },
        createNewWishlist() {
            if (!this.newWishlistName || this.isCreating) {
                return;
            }

            this.isCreating = true;

            client
                .post("/vytvorit-zoznam", {
                    name: this.newWishlistName,
                    product_id: this.productId,
                })
                .then((response) => {
                    if (response.data.status) {
                        window.Eshop?.alert?.(response.data.message, {
                            type: "success",
                        });

                        const newWish = {
                            id: response.data.wishlist_id,
                            name: this.newWishlistName,
                            is_present: "1",
                        };
                        this.wishlists = [...this.wishlists, newWish];

                        this.showNewWishlistForm = false;
                        this.newWishlistName = "";
                    } else {
                        window.Eshop?.alert?.(response.data.message, {
                            type: "error",
                        });
                    }
                })
                .catch((error) => {
                    console.error("Failed to create wishlist:", error);
                })
                .finally(() => {
                    this.isCreating = false;
                });
        },
        fetchWishlists() {
            this.isLoadingWishlists = true;

            client
                .get("/api/moje-zoznamy", { params: { id: this.productId } })
                .then((response) => {
                    this.wishlists = response.data.data || [];
                    this.isLoadingWishlists = false;
                })
                .catch((error) => {
                    console.error("Failed to fetch wishlists:", error);
                    this.isLoadingWishlists = false;
                });
        },
        handleClickOutside(event) {
            const popup = this.$el?.querySelector?.(".c-wishlist-popup__content");
            const trigger = this.$el?.querySelector?.(".c-wishlist-popup__trigger");
            if (
                this.isPopupVisible &&
                popup &&
                !popup.contains(event.target) &&
                trigger &&
                !trigger.contains(event.target)
            ) {
                this.isPopupVisible = false;
            }
        },
        toggleWishlistItem(event, wish) {
            const isChecked = event.target.checked;
            const url = isChecked ? "/wishlists/add" : "/wishlists/delete-product";

            event.target.disabled = true;
            event.target.classList.add("is-loading");

            client
                .post(url, {
                    product_id: this.productId,
                    customer_wishlist_id: wish.id,
                })
                .then((response) => {
                    window.Eshop?.alert?.(response.data.message, {
                        type: response.data.status ? "success" : "error",
                    });

                    if (response.data.status) {
                        wish.is_present = isChecked ? "1" : "0";
                    } else {
                        event.target.checked = !isChecked;
                    }

                    event.target.disabled = false;
                    event.target.classList.remove("is-loading");
                })
                .catch((error) => {
                    console.error("Wishlist update failed:", error);
                    event.target.checked = !isChecked;
                    event.target.disabled = false;
                    event.target.classList.remove("is-loading");
                });
        },
        showCreateForm() {
            this.showNewWishlistForm = true;
            this.$nextTick(() => {
                this.$refs.newWishlistInput?.focus();
            });
        },
    },
};
</script>
